/* Doctor Signup — sandbox → real portal claim wizard (iter100.157ah)
 * ────────────────────────────────────────────────────────────────
 *  Route:  /doctor-signup
 *  Guard:  requires the caller to be a sandbox visitor (is_demo=true).
 *          Non-demo users get bounced to /doctor.
 *
 *  Three steps:
 *    1. Phone + OTP   — pre-fills from user.claim_phone
 *    2. Confirm clinic — pre-fills from the current sandbox doctor
 *                       profile; user can edit before promoting.
 *    3. Success       — green tick, "Your data is now yours" copy,
 *                       auto-redirects to /doctor after 3 seconds.
 *
 *  Everything the visitor built in sandbox (patients, cases, landing
 *  page config, brand colours) stays with them — the account is simply
 *  flipped from `is_demo:true` to `is_demo:false` on the server.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, ArrowRight, Sparkle, ArrowLeft, Flask } from "@phosphor-icons/react";


// ── Steps enum for clarity in the render switch. ────────────────────
const STEP = { PHONE: 1, CONFIRM: 2, SUCCESS: 3 };


export default function DoctorSignupPage() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Guard — only sandbox visitors should be here.
    useEffect(() => {
        if (user && !user.is_demo) {
            navigate("/doctor", { replace: true });
        }
    }, [user, navigate]);

    const [step, setStep] = useState(STEP.PHONE);
    // Pre-fill from the claim_phone the visitor entered when they
    // originally opened the demo link. Falls back to blank.
    const [phone, setPhone] = useState(() => user?.claim_phone || "");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [devCode, setDevCode] = useState(null);
    const [name, setName] = useState(() => user?.name || "");
    const [clinicName, setClinicName] = useState("");
    const [city, setCity] = useState("");
    const [busy, setBusy] = useState(false);
    const fromSandbox = searchParams.get("from") === "sandbox";

    // ── Step 1 — send OTP ───────────────────────────────────────────
    const handleSendOtp = async () => {
        if (!phone.trim()) {
            toast.error("Enter your phone number to receive an OTP.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/sandbox/claim-real/send-otp", { phone: phone.trim() });
            setOtpSent(true);
            setDevCode(data.dev_code || null);
            toast.success(data.dev_mode ? `Dev OTP: ${data.dev_code}` : "OTP sent — check your phone.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send the OTP. Try again.");
        } finally {
            setBusy(false);
        }
    };

    // ── Step 1 → 2 — verify OTP by attempting confirm with minimal
    //    fields. We defer the actual full claim until step 2 so the
    //    user can review clinic details, but we validate the OTP now.
    //    Approach: call confirm with just phone/code, no updates —
    //    server will flip the account, then step 2 lets the user
    //    review + PATCH their profile via existing doctor endpoints
    //    if they want to change anything. This keeps the backend
    //    surface small (one confirm endpoint, one round-trip).
    const handleVerifyAndAdvance = async () => {
        if (otp.length < 4) {
            toast.error("Enter the OTP you received.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/sandbox/claim-real/confirm", {
                phone: phone.trim(),
                code: otp.trim(),
                name: name.trim() || undefined,
            });
            // Refresh auth session — the account is now real, and the
            // new JWT carries an is_demo=false claim.
            login(data.token, data.user);
            setStep(STEP.CONFIRM);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "That OTP didn't match. Try again.");
        } finally {
            setBusy(false);
        }
    };

    // ── Step 2 → 3 — persist clinic details, then celebrate. ────────
    const handleConfirmClinic = async () => {
        setBusy(true);
        try {
            // Optional: patch the doctor profile with the confirmed
            // clinic details. Wrap in a soft-fail so a partial profile
            // save doesn't block the celebration screen.
            if (clinicName.trim() || city.trim() || name.trim()) {
                try {
                    await api.patch("/doctor/profile", {
                        name: name.trim() || undefined,
                        full_name: name.trim() || undefined,
                        clinic_name: clinicName.trim() || undefined,
                        city: city.trim() || undefined,
                    });
                } catch (patchErr) {
                    // Non-blocking — the primary flip already happened.
                    console.warn("[doctor-signup] Clinic patch failed:", patchErr);
                }
            }
            setStep(STEP.SUCCESS);
            // Redirect after the celebration.
            setTimeout(() => navigate("/doctor", { replace: true }), 3200);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50 grid place-items-center p-4">
            <div className="w-full max-w-lg">
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold tracking-[0.16em] uppercase mb-4">
                        <Flask size={11} weight="fill" />
                        {fromSandbox ? "Sandbox → Real portal" : "Claim your portal"}
                    </div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                        {step === STEP.SUCCESS ? "Welcome to Invisique™" : "Claim your real portal"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                        {step === STEP.PHONE && "Verify your phone. Your demo data stays with you — nothing gets reset."}
                        {step === STEP.CONFIRM && "Confirm your clinic details. You can change these later."}
                        {step === STEP.SUCCESS && "Your patient data is yours, not ours. Redirecting you…"}
                    </p>
                </div>

                {/* Step indicator */}
                {step !== STEP.SUCCESS && (
                    <div className="flex items-center justify-center gap-2 mb-6" data-testid="doctor-signup-stepper">
                        {[STEP.PHONE, STEP.CONFIRM].map((n) => (
                            <div
                                key={n}
                                className={`h-1.5 rounded-full transition-all ${
                                    step >= n ? "w-10 bg-violet-600" : "w-6 bg-slate-200"
                                }`}
                            />
                        ))}
                    </div>
                )}

                {/* Card */}
                <div className="rounded-3xl bg-card border border-border shadow-lg p-6 sm:p-8">
                    {step === STEP.PHONE && (
                        <div className="space-y-5" data-testid="doctor-signup-step-phone">
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                                    Your phone number
                                </label>
                                <Input
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="10-digit mobile · +country-code for others"
                                    disabled={otpSent || busy}
                                    className="mt-2 h-11"
                                    data-testid="doctor-signup-phone-input"
                                />
                            </div>

                            {!otpSent ? (
                                <Button
                                    type="button"
                                    onClick={handleSendOtp}
                                    disabled={busy || !phone.trim()}
                                    className="w-full h-11 font-semibold bg-violet-700 hover:bg-violet-800 text-white"
                                    data-testid="doctor-signup-send-otp-btn"
                                >
                                    {busy ? "Sending…" : "Send OTP"}
                                    <ArrowRight size={13} weight="bold" className="ml-1.5" />
                                </Button>
                            ) : (
                                <>
                                    <div>
                                        <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                                            Enter the 6-digit code
                                        </label>
                                        <Input
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                            placeholder="123456"
                                            maxLength={6}
                                            inputMode="numeric"
                                            className="mt-2 h-11 tabular-nums tracking-[0.3em] text-center font-semibold"
                                            data-testid="doctor-signup-otp-input"
                                        />
                                        {devCode && (
                                            <p className="text-[11px] text-emerald-600 mt-1.5">
                                                Dev mode — code: <span className="font-mono font-bold">{devCode}</span>
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => { setOtpSent(false); setOtp(""); }}
                                            disabled={busy}
                                            className="h-11"
                                            data-testid="doctor-signup-change-phone-btn"
                                        >
                                            <ArrowLeft size={13} weight="bold" className="mr-1" />
                                            Change phone
                                        </Button>
                                        <Button
                                            type="button"
                                            onClick={handleVerifyAndAdvance}
                                            disabled={busy || otp.length < 4}
                                            className="flex-1 h-11 font-semibold bg-violet-700 hover:bg-violet-800 text-white"
                                            data-testid="doctor-signup-verify-btn"
                                        >
                                            {busy ? "Verifying…" : "Verify & continue"}
                                            <ArrowRight size={13} weight="bold" className="ml-1.5" />
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {step === STEP.CONFIRM && (
                        <div className="space-y-4" data-testid="doctor-signup-step-confirm">
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                                    Your name
                                </label>
                                <Input
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Dr. Priya Nair"
                                    className="mt-2 h-11"
                                    data-testid="doctor-signup-name-input"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                                    Clinic name
                                </label>
                                <Input
                                    value={clinicName}
                                    onChange={(e) => setClinicName(e.target.value)}
                                    placeholder="Nair Orthodontic Center"
                                    className="mt-2 h-11"
                                    data-testid="doctor-signup-clinic-input"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                                    City
                                </label>
                                <Input
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="Mumbai"
                                    className="mt-2 h-11"
                                    data-testid="doctor-signup-city-input"
                                />
                            </div>
                            <Button
                                type="button"
                                onClick={handleConfirmClinic}
                                disabled={busy}
                                className="w-full h-11 font-semibold bg-violet-700 hover:bg-violet-800 text-white mt-3"
                                data-testid="doctor-signup-confirm-btn"
                            >
                                {busy ? "Saving…" : "Claim my portal"}
                                <Sparkle size={13} weight="fill" className="ml-1.5" />
                            </Button>
                            <p className="text-[11px] text-center text-muted-foreground">
                                You can edit any of this later in your Provider Profile.
                            </p>
                        </div>
                    )}

                    {step === STEP.SUCCESS && (
                        <div className="text-center py-6" data-testid="doctor-signup-step-success">
                            {/* Animated green tick */}
                            <div className="mx-auto w-20 h-20 mb-4 relative">
                                <div className="absolute inset-0 rounded-full bg-emerald-100 animate-ping opacity-40" />
                                <div className="absolute inset-0 rounded-full bg-emerald-500 grid place-items-center shadow-lg">
                                    <CheckCircle size={44} weight="fill" className="text-white animate-in zoom-in duration-500" />
                                </div>
                            </div>
                            <div className="font-display text-xl font-bold tracking-tight">
                                Your portal is yours.
                            </div>
                            <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
                                Everything you built in sandbox is preserved. Real patients, real KPIs, real bookings — starting now.
                            </p>
                            <p className="text-[11px] text-muted-foreground mt-4">
                                Redirecting to your dashboard…
                            </p>
                        </div>
                    )}
                </div>

                {step === STEP.PHONE && (
                    <p className="text-[11px] text-center text-muted-foreground mt-4">
                        Already have a real Invisique account?{" "}
                        <button
                            type="button"
                            onClick={() => navigate("/doctor-login")}
                            className="text-violet-700 font-semibold hover:underline"
                            data-testid="doctor-signup-existing-login-link"
                        >
                            Log in instead
                        </button>
                    </p>
                )}
            </div>
        </div>
    );
}
